import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { ReactNode, useState } from "react";
import { API_URL } from "./Constants";
import { Volleyball } from "./Icons";
import { SignupSummary } from "./summary";

const CANCELLATION_POLICY_URL =
  "https://help.volosports.com/support/solutions/articles/29000049372-late-cancellations-no-shows-and-check-in-expectations-strike-system-";

// The acknowledgements. Keys must match what /enable-signup enforces. The Volo
// Pass requirement isn't a checkbox — it's verified server-side.
const CONSENTS: { key: string; label: ReactNode }[] = [
  {
    key: "registersAndSignsWaivers",
    label:
      "I authorize VoloPilot to register me for volleyball pickups on my behalf, " +
      "including agreeing to Volo's waiver for me.",
  },
  {
    key: "cancellationPolicy",
    label: (
      <>
        I understand Volo's{" "}
        <a
          href={CANCELLATION_POLICY_URL}
          target="_blank"
          rel="noreferrer"
        >
          cancellation and no-show policy
        </a>{" "}
        and may be charged if I don't show up.
      </>
    ),
  },
];

// ISO weekdays, matching volocal/src/signup_days.ts (1 = Mon … 7 = Sun).
const DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];
const ALL_DAYS = DAYS.map((d) => d.value);

// Which days to auto-sign-up on. Without this, turning auto-signup on means
// every pickup in the city, which is a lot of games (and Volo charges for late
// cancels), so this is the control that makes it safe to leave on.
const DayPicker = ({
  signupDays,
  onSave,
  saving,
}: {
  signupDays: number[] | null;
  onSave: (days: number[]) => void;
  saving: boolean;
}) => {
  // null from the server means "no filter", which shows as all days selected.
  const [days, setDays] = useState<number[]>(signupDays ?? ALL_DAYS);
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Sign me up on these days:
      </Typography>
      <ToggleButtonGroup
        value={days}
        onChange={(_e, next: number[]) => {
          // Refuse to leave nothing selected: that reads as "off", and Stop is
          // the control for that.
          if (!next.length) return;
          setDays(next);
          onSave(next);
        }}
        size="small"
        disabled={saving}
        sx={{ flexWrap: "wrap" }}
      >
        {DAYS.map((d) => (
          <ToggleButton key={d.value} value={d.value} sx={{ px: 1.5 }}>
            {d.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

// Matching volocal/src/signup_prefs.ts.
const LEVELS = [
  { value: "beginner", label: "Beginner/Social" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced/Competitive" },
];
const ALL_LEVELS = LEVELS.map((l) => l.value);

// Start times in minutes after midnight, 5pm to 11pm in 15-minute steps.
// Pickups run in the evening (6:00–9:30pm in SF), often on a quarter hour.
const TIMES = Array.from({ length: 25 }, (_, i) => 17 * 60 + i * 15);
const timeLabel = (m: number) => {
  const h = Math.floor(m / 60) % 12 || 12;
  const mm = m % 60;
  return `${h}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${m < 12 * 60 ? "am" : "pm"}`;
};

export type SignupFilters = {
  neighborhoods: string[] | null;
  levels: string[] | null;
  earliest: number | null;
  latest: number | null;
};

// A multi-choice row like the day picker. Nothing selected isn't allowed —
// that would read as "off", and Stop is the control for that.
const ChoiceRow = ({
  label,
  options,
  selected,
  onChange,
  disabled,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="body2" sx={{ mb: 1 }}>
      {label}
    </Typography>
    <ToggleButtonGroup
      value={selected}
      onChange={(_e, next: string[]) => {
        if (!next.length) return;
        onChange(next);
      }}
      size="small"
      disabled={disabled}
      sx={{ flexWrap: "wrap" }}
    >
      {options.map((o) => (
        <ToggleButton key={o.value} value={o.value} sx={{ px: 1.5 }}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

// Where, what level, and when. Every one defaults to "any"; an unset filter
// shows as everything selected.
const FilterPicker = ({
  filters,
  neighborhoodOptions,
  onSave,
  saving,
}: {
  filters: SignupFilters;
  neighborhoodOptions: string[];
  onSave: (change: Partial<SignupFilters>) => void;
  saving: boolean;
}) => {
  const [neighborhoods, setNeighborhoods] = useState<string[]>(
    filters.neighborhoods ?? neighborhoodOptions
  );
  const [levels, setLevels] = useState<string[]>(filters.levels ?? ALL_LEVELS);
  const [earliest, setEarliest] = useState<number | null>(filters.earliest);
  const [latest, setLatest] = useState<number | null>(filters.latest);

  const saveTimes = (e: number | null, l: number | null) => {
    setEarliest(e);
    setLatest(l);
    onSave({ earliest: e, latest: l });
  };
  const timeField = (
    label: string,
    value: number | null,
    onChange: (v: number | null) => void,
    // Keep the two ends from crossing.
    allowed: (m: number) => boolean
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value ?? ""}
      disabled={saving}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : Number(e.target.value))
      }
      sx={{ minWidth: 130 }}
    >
      <MenuItem value="">Any time</MenuItem>
      {TIMES.filter(allowed).map((m) => (
        <MenuItem key={m} value={m}>
          {timeLabel(m)}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Starting between:
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {timeField("Earliest", earliest, (v) => saveTimes(v, latest), (m) =>
            latest === null ? true : m <= latest
          )}
          <Typography variant="body2">and</Typography>
          {timeField("Latest", latest, (v) => saveTimes(earliest, v), (m) =>
            earliest === null ? true : m >= earliest
          )}
        </Stack>
      </Box>
      {neighborhoodOptions.length > 1 && (
        <ChoiceRow
          label="In these neighborhoods:"
          options={neighborhoodOptions.map((n) => ({ value: n, label: n }))}
          selected={neighborhoods}
          disabled={saving}
          onChange={(next) => {
            setNeighborhoods(next);
            onSave({ neighborhoods: next });
          }}
        />
      )}
      <ChoiceRow
        label="At these levels:"
        options={LEVELS}
        selected={levels}
        disabled={saving}
        onChange={(next) => {
          setLevels(next);
          onSave({ levels: next });
        }}
      />
    </>
  );
};

// Mirrors volocal/src/pause.ts so the website and Discord panel offer the same
// spans. Default is "2w".
const PAUSE_OPTIONS = [
  { key: "1w", label: "1 week" },
  { key: "2w", label: "2 weeks" },
  { key: "1mo", label: "1 month" },
  { key: "indef", label: "Until I resume" },
];

export const AutoSignupCard = ({
  consented,
  paused,
  pausedUntil,
  hasVoloPass,
  signupDays,
  filters,
  neighborhoodOptions,
  refetch,
}: {
  consented: boolean;
  paused: boolean;
  pausedUntil: string | null;
  hasVoloPass: boolean;
  signupDays: number[] | null;
  filters: SignupFilters;
  neighborhoodOptions: string[];
  refetch: () => void;
}) => {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [duration, setDuration] = useState("2w");
  const [pauseOpen, setPauseOpen] = useState(false);
  // One handler for pause/resume/stop — all POST, all refetch.
  const action = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: object }) => {
      const res = await fetch(API_URL + path, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.status !== 200) {
        throw new Error(await res.text());
      }
      refetch();
    },
  });
  const mutation = useMutation({
    mutationFn: async (consents: Record<string, boolean>) => {
      const res = await fetch(API_URL + "/enable-signup", {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ consents }),
        headers: { "Content-Type": "application/json" },
      });
      if (res.status !== 200) {
        throw new Error(await res.text());
      }
      refetch();
    },
  });
  const allChecked = CONSENTS.every((c) => checked[c.key]);

  if (consented) {
    const pausedLabel = pausedUntil
      ? "until " +
        new Date(pausedUntil).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      : "until you resume";
    // From the saved settings, so it updates once a click has been saved
    // and says what will really happen.
    const summaryProps = { days: signupDays, ...filters };
    return (
      <Card>
        <Box padding="2em">
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={{ color: "secondary.main", display: "flex" }}>
              <Volleyball width={22} height={22} />
            </Box>
            <Typography variant="h5">Auto-signup</Typography>
          </Stack>

          {/* Status first: what is it doing right now. */}
          {paused ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Auto-signup is paused {pausedLabel}. When it resumes,{" "}
              <SignupSummary {...summaryProps} />
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mt: 2 }}>
              Auto-signup is on. <SignupSummary {...summaryProps} />
            </Alert>
          )}

          {action.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {action.error.message || "Something went wrong."}
            </Alert>
          )}

          {/* Then the setting you'd actually come here to change. */}
          <DayPicker
            signupDays={signupDays}
            saving={action.isPending}
            onSave={(days) =>
              action.mutate({ path: "/signup-days", body: { days } })
            }
          />
          <FilterPicker
            filters={filters}
            neighborhoodOptions={neighborhoodOptions}
            saving={action.isPending}
            onSave={(change) =>
              action.mutate({ path: "/signup-prefs", body: change })
            }
          />

          <Divider sx={{ mt: 3 }} />

          {/* Then the actions, with the irreversible one set apart at the end. */}
          {paused ? (
            <LoadingButton
              loading={action.isPending}
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => action.mutate({ path: "/resume" })}
            >
              Resume auto-signup
            </LoadingButton>
          ) : (
            <>
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                Going away for a while?
              </Typography>
              <Button
                variant="contained"
                disabled={action.isPending}
                onClick={() => setPauseOpen(true)}
              >
                Pause auto-signup
              </Button>
            </>
          )}

          <Dialog
            open={pauseOpen}
            onClose={() => setPauseOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Pause auto-signup</DialogTitle>
            <DialogContent>
              <RadioGroup
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              >
                {PAUSE_OPTIONS.map((o) => (
                  <FormControlLabel
                    key={o.key}
                    value={o.key}
                    control={<Radio />}
                    label={o.label}
                  />
                ))}
              </RadioGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPauseOpen(false)}>Cancel</Button>
              <LoadingButton
                loading={action.isPending}
                variant="contained"
                onClick={() =>
                  action.mutate(
                    { path: "/pause", body: { duration } },
                    { onSuccess: () => setPauseOpen(false) }
                  )
                }
              >
                Pause
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box padding="2em">
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box sx={{ color: "secondary.main", display: "flex" }}>
            <Volleyball width={22} height={22} />
          </Box>
          <Typography variant="h5">Auto-signup</Typography>
        </Stack>
        <Typography variant="body1" marginTop="1em">
          Get registered for new volleyball pickups automatically, the moment
          they open — even while you're asleep.
        </Typography>

        {!hasVoloPass ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Auto-signup needs an active <b>Volo Pass</b> — without one, Volo
            charges for every pickup. Grab a{" "}
            <a
              href="https://www.volosports.com/volo-pass"
              target="_blank"
              rel="noreferrer"
            >
              Volo Pass
            </a>
            , then reload this page.
          </Alert>
        ) : (
          <>
            {mutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {mutation.error.message || "Couldn't enable auto-signup."}
              </Alert>
            )}

            <Stack marginTop={2} spacing={1}>
              {CONSENTS.map((c) => (
                <FormControlLabel
                  key={c.key}
                  control={
                    <Checkbox
                      checked={!!checked[c.key]}
                      onChange={(e) =>
                        setChecked((s) => ({ ...s, [c.key]: e.target.checked }))
                      }
                    />
                  }
                  label={<Typography variant="body2">{c.label}</Typography>}
                />
              ))}
            </Stack>

            <LoadingButton
              loading={mutation.isPending}
              disabled={!allChecked}
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() =>
                mutation.mutate(
                  Object.fromEntries(
                    CONSENTS.map((c) => [c.key, !!checked[c.key]])
                  )
                )
              }
            >
              Turn on auto-signup
            </LoadingButton>
          </>
        )}
      </Box>
    </Card>
  );
};
